import { useInfiniteQuery } from '@tanstack/react-query';
import { getSdk, resolveApiBaseUrl } from '@/lib/sdk';
import type { FeedFilter, Post } from '@/types';

const FEED_PAGE_SIZE = 20;

export const feedKeys = {
  all: ['feed'] as const,
  list: (type: FeedFilter) => ['feed', type] as const,
  songs: () => ['feed', 'songs'] as const,
  videos: () => ['feed', 'videos'] as const,
  bursts: () => ['feed', 'bursts'] as const,
  genre: (genre: string) => ['feed', 'genre', genre] as const,
  profileFeed: (username: string) => ['feed', 'profile', username] as const,
  profileSongs: (username: string) => ['feed', 'profileSongs', username] as const,
  profilePosts: (username: string) => ['feed', 'profilePosts', username] as const,
  profileImages: (username: string) => ['feed', 'profileImages', username] as const,
};

/**
 * Map feed filter to the correct API endpoint.
 * Uses /v1/feeds/* endpoints (post feeds) not /v1/songs/feed/* (music-only).
 */
function feedEndpoint(feedType: FeedFilter): string {
  switch (feedType) {
    case 'following':
      return '/v1/feeds/following';
    case 'trending':
      return '/v1/feeds/influencers';
    case 'latest':
      return '/v1/feeds/posts';
    case 'discover':
      return '/v1/feeds/timeline';
    case 'forYou':
    default:
      return '/v1/feeds/timeline';
  }
}

/** Endpoints that support cursor-based pagination. */
const PAGINATED_ENDPOINTS = new Set([
  '/v1/feeds/posts',
  '/v1/feeds/following',
  '/v1/feeds/influencers',
]);

/**
 * Extract next_cursor from API envelope (handles both snake_case and camelCase).
 */
function extractCursor(envelope: Record<string, unknown>): string | undefined {
  return (
    (envelope.next_cursor as string) ??
    (envelope.nextCursor as string) ??
    ((envelope.meta as Record<string, unknown> | undefined)?.next_cursor as string) ??
    ((envelope.meta as Record<string, unknown> | undefined)?.nextCursor as string) ??
    undefined
  );
}

/**
 * Main feed (For You, Following, Trending, etc.) with cursor-based infinite scroll.
 *
 * Two-step fetch matching the Vue app pattern:
 * 1. Fetch feed endpoint → ULIDs + next_cursor
 * 2. POST /v1/posts with ULIDs → full Post objects
 */
export function useFeedInfinite(feedType: FeedFilter) {
  const sdk = getSdk();
  const endpoint = feedEndpoint(feedType);

  return useInfiniteQuery({
    queryKey: feedKeys.list(feedType),
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam || undefined;
      // Use SDK's fetchFeedPage with a smaller page size
      const response = await sdk.fetchFeedPage(cursor, endpoint, endpoint, FEED_PAGE_SIZE);

      // The SDK's extractNextCursor only checks camelCase, but API returns snake_case.
      // Access the raw envelope via the SDK's internal response to get next_cursor.
      // Since we can't access the raw envelope, re-derive: if SDK returned null cursor
      // but we got a full page, the cursor is likely available as snake_case.
      let nextCursor = response.nextCursor ?? undefined;

      // Only attempt cursor extraction for endpoints that support pagination.
      // /v1/feeds/timeline returns no pagination — skip the fallback fetch.
      if (!nextCursor && response.posts.length >= FEED_PAGE_SIZE && PAGINATED_ENDPOINTS.has(endpoint)) {
        // SDK's extractNextCursor only checks camelCase but API returns snake_case.
        // Fetch the raw envelope to get next_cursor.
        const params = new URLSearchParams();
        if (cursor) params.set('cursor', cursor);
        params.set('limit', String(FEED_PAGE_SIZE));
        const rawResp = await fetch(
          `${resolveApiBaseUrl()}${endpoint}?${params}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
              Accept: 'application/json',
            },
          },
        );
        if (rawResp.ok) {
          const rawJson = await rawResp.json() as Record<string, unknown>;
          nextCursor = extractCursor(rawJson);
        }
      }

      return {
        posts: (response.posts || []) as Post[],
        nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Songs feed (type=SONG posts).
 */
export function useSongsFeed() {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.songs(),
    queryFn: async ({ pageParam }) =>
      sdk.fetchFeedPage(pageParam || undefined, '/v1/songs/feed/all', 'songs', FEED_PAGE_SIZE),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Videos feed (type=VIDEO posts).
 */
export function useVideosFeed() {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.videos(),
    queryFn: async ({ pageParam }) =>
      sdk.fetchFeedPage(pageParam || undefined, '/v1/videos/all', 'videos', FEED_PAGE_SIZE),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Bursts/Shorts feed (type=BURST posts).
 */
export function useBurstsFeed() {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.bursts(),
    queryFn: async ({ pageParam }) =>
      sdk.fetchFeedPage(pageParam || undefined, '/v1/bursts/all', 'bursts', FEED_PAGE_SIZE),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Genre-specific feed.
 */
export function useGenreFeed(genrePath: string) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.genre(genrePath),
    queryFn: async ({ pageParam }) =>
      sdk.fetchGenreFeed(genrePath, pageParam || undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(genrePath),
  });
}

/**
 * User profile feed.
 */
export function useProfileFeed(username: string) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.profileFeed(username),
    queryFn: async ({ pageParam }) =>
      sdk.getProfileFeed(username, pageParam || undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(username),
  });
}

/**
 * User's songs.
 */
export function useProfileSongs(username: string) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.profileSongs(username),
    queryFn: async ({ pageParam }) =>
      sdk.getProfileSongs(username, pageParam || undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(username),
  });
}

/**
 * User's posts only (no songs/videos).
 */
export function useProfilePosts(username: string) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.profilePosts(username),
    queryFn: async ({ pageParam }) =>
      sdk.getProfilePostsOnly(username, pageParam || undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(username),
  });
}

/**
 * User's images/media.
 */
export function useProfileImages(username: string) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: feedKeys.profileImages(username),
    queryFn: async ({ pageParam }) =>
      sdk.getProfileImages(username, pageParam || undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(username),
  });
}
